import { PickOrderDetailFragment } from '../../../../graphql'
import { productIdentity, ProductIdentity } from '../productIdentity'

type Fulfillment = PickOrderDetailFragment['fulfillments'][number]

export type PackingSlipLine = {
	key: string,
	identity: ProductIdentity,
	orderNumber: number | string,
	requestNumber: number | string | null,
	quantity: number,
}

export type PackingSlip = {
	fulfillmentId: string,
	cartonNumber: number | null,
	transferOrderId: string | null,
	trackingNumber: string | null,
	orderNumbers: ( number | string )[],
	requestNumbers: ( number | string )[],
	lines: PackingSlipLine[],
	units: number,
}

function unique<T>( values: T[] ): T[] {
	return [ ...new Set( values ) ]
}

function slipFor( fulfillment: Fulfillment ): PackingSlip {
	// A line reporting only a shortfall put nothing in the box.
	const lines = fulfillment.lines
		.filter( ( line ) => line.quantityFulfilled > 0 )
		.map( ( line ) => ( {
			key: line.id,
			identity: productIdentity( line.fulfillmentOrderLineItem.product ),
			orderNumber: line.fulfillmentOrderLineItem.fulfillmentOrder.orderNumber,
			requestNumber: line.fulfillmentOrderLineItem.demandRequestLineItem?.demandRequest.requestNumber ?? null,
			quantity: line.quantityFulfilled,
		} ) )
		.sort( ( a, b ) => ( a.identity.sku ?? '' ).localeCompare( b.identity.sku ?? '', undefined, { numeric: true } ) )

	return {
		fulfillmentId: fulfillment.id,
		cartonNumber: fulfillment.cartonNumber ?? null,
		transferOrderId: fulfillment.systemTransferOrder?.id ?? null,
		trackingNumber: fulfillment.trackingNumber ?? null,
		orderNumbers: unique( lines.map( ( line ) => line.orderNumber ) ).sort( ( a, b ) => Number( a ) - Number( b ) ),
		requestNumbers: unique( lines.map( ( line ) => line.requestNumber ).filter( ( number ): number is number | string => number !== null ) )
			.sort( ( a, b ) => Number( a ) - Number( b ) ),
		lines,
		units: lines.reduce( ( sum, line ) => sum + line.quantity, 0 ),
	}
}

/**
 * One slip per carton that has something in it, in carton order. A carton that
 * only reported shortfall held nothing, so it gets no slip.
 */
export function packingSlips( fulfillments: Fulfillment[] ): PackingSlip[] {
	return [ ...fulfillments ]
		.sort( ( a, b ) => ( a.cartonNumber ?? 0 ) - ( b.cartonNumber ?? 0 ) )
		.map( slipFor )
		.filter( ( slip ) => slip.units > 0 )
}
